import React from 'react';
import { TFunction } from 'react-i18next';

import CobaltTag from '@cobalt/react-tag';

import CasePriorityTag, { CasePriorityTagProps } from './CasePriorityTag';
import { SelectOptionType } from '../select';

export enum CasePriorityEnum {
  Low = '001',
  Normal = '002',
  High = '003',
  Urgent = '004',
}

type TagColor = React.ComponentProps<typeof CobaltTag>;
type CasePriorityType = { value: CasePriorityEnum; text: string; color: TagColor['color'] };
type CasePriorityMap = Record<CasePriorityEnum, CasePriorityType>;

export function genCasePriorityMap(t: TFunction): CasePriorityMap {
  const casePriorityTextMap = {
    [CasePriorityEnum.Low]: t('component.case_priority.low', { defaultValue: 'Low' }),
    [CasePriorityEnum.Normal]: t('component.case_priority.normal', { defaultValue: 'Normal' }),
    [CasePriorityEnum.High]: t('component.case_priority.high', { defaultValue: 'High' }),
    [CasePriorityEnum.Urgent]: t('component.case_priority.urgent', { defaultValue: 'Urgent' }),
  };

  return {
    [CasePriorityEnum.Low]: {
      value: CasePriorityEnum.Low,
      text: casePriorityTextMap[CasePriorityEnum.Low],
      color: 'light-green',
    },
    [CasePriorityEnum.Normal]: {
      value: CasePriorityEnum.Normal,
      text: casePriorityTextMap[CasePriorityEnum.Normal],
      color: undefined,
    },
    [CasePriorityEnum.High]: {
      value: CasePriorityEnum.High,
      text: casePriorityTextMap[CasePriorityEnum.High],
      color: 'light-yellow',
    },
    [CasePriorityEnum.Urgent]: {
      value: CasePriorityEnum.Urgent,
      text: casePriorityTextMap[CasePriorityEnum.Urgent],
      color: 'light-red',
    },
  };
}

export function genCasePriorityOptions<T extends SelectOptionType>(
  options: T[],
  t: TFunction,
  size?: CasePriorityTagProps['size'],
): T[] {
  const casePriorityMap = genCasePriorityMap(t);

  return options.map((option) => ({
    ...option,
    label: casePriorityMap[option.value as CasePriorityEnum].text,
    renderOption: () => <CasePriorityTag priority={option.value as CasePriorityEnum} size={size} />,
  }));
}
